import React, { useState } from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface ClearSessionConfirmDialogProps {
  isOpen: boolean;
  messageCount: number;
  sessionCount: number;
  isRunning: boolean;
  onConfirm: (stopWorkflow: boolean) => void;
  onCancel: () => void;
}

const ClearSessionConfirmDialog: React.FC<ClearSessionConfirmDialogProps> = ({
  isOpen,
  messageCount,
  sessionCount,
  isRunning,
  onConfirm,
  onCancel,
}) => {
  const [stopWorkflow, setStopWorkflow] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(stopWorkflow);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-black bg-opacity-75"
          onClick={onCancel}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-cap-dark shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">
                Clear Session Data
              </h3>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Warning message */}
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm text-red-800 dark:text-red-300 font-medium mb-2">
              This action cannot be undone
            </p>
            <p className="text-sm text-red-700 dark:text-red-400">
              You are about to clear:
            </p>
            <ul className="mt-2 ml-4 text-sm text-red-700 dark:text-red-400 list-disc">
              <li>{messageCount} message{messageCount !== 1 ? 's' : ''}</li>
              <li>{sessionCount} session{sessionCount !== 1 ? 's' : ''}</li>
            </ul>
          </div>

          {/* Enhanced warning for running workflow */}
          {isRunning && (
            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-2">
                Workflow is currently running
              </p>
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="stopWorkflow"
                  checked={stopWorkflow}
                  onChange={(e) => setStopWorkflow(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label htmlFor="stopWorkflow" className="text-sm text-amber-700 dark:text-amber-300">
                  Also stop the running workflow
                </label>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Clear Session Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClearSessionConfirmDialog;
